/**
 * The stage's arithmetic: how many columns, how big a tile, who stands where
 * and what a screen reader is told. Kept apart from the components so the
 * rules in _design/sketches/Meetings.md §4, §6 and §9 can be read, and
 * tested, without a meeting.
 */

import type { PeerState } from '@/hooks/callPeers'

/**
 * A phone has one column up to two people and two after that. Everything
 * wider has two to four people, three to six and four to eight.
 */
export function stageColumns(count: number, compact: boolean): number {
  if (compact) return count <= 2 ? 1 : 2
  if (count <= 1) return 1
  if (count <= 4) return 2
  if (count <= 6) return 3
  return 4
}

/**
 * The largest 4:3 tile that lets every row fit the stage. The stage has a
 * fixed size and the tiles are fitted into it, never the other way round: a
 * stage that grows with its tiles moves the controls each time somebody joins.
 */
export function fitTile({
  width,
  height,
  count,
  columns,
  gap,
}: {
  width: number
  height: number
  count: number
  columns: number
  gap: number
}): { width: number; height: number } {
  const rows = Math.max(1, Math.ceil(count / columns))
  const byWidth = (width - gap * (columns - 1)) / columns
  const byHeight = ((height - gap * (rows - 1)) / rows) * (4 / 3)
  const fitted = Math.floor(Math.max(0, Math.min(byWidth, byHeight)))
  return { width: fitted, height: Math.floor((fitted * 3) / 4) }
}

/**
 * Tiles never reorder while the meeting runs: a joiner is appended and a
 * leaver's slot closes. `present` is whoever is there now, in any order.
 */
export function joinOrder(previous: readonly string[], present: readonly string[]): string[] {
  const here = new Set(present)
  const kept = previous.filter((id) => here.has(id))
  const known = new Set(kept)
  return [...kept, ...present.filter((id) => !known.has(id))]
}

/** Tile state and name as one string, the way it is read out. */
export function tileAccessibleName(tile: {
  name: string
  muted: boolean
  speaking: boolean
  sharing: boolean
  state: PeerState | null
}): string {
  const parts = [tile.name]
  if (tile.sharing) parts.push('sharing a screen')
  if (tile.muted) parts.push('muted')
  if (tile.state === 'connecting') parts.push('connecting')
  else if (tile.state === 'reconnecting') parts.push('reconnecting')
  else if (tile.state === 'failed') parts.push('could not connect')
  else if (tile.speaking) parts.push('speaking')
  return parts.join(', ')
}

export const RECONNECTING_AFTER_MS = 2_000
export const FAILED_AFTER_MS = 20_000

/**
 * What a tile says about its connection, given how long it has been without
 * one. A blip under two seconds says nothing; twenty seconds is a failure
 * whatever the browser still hopes for.
 */
export function peerPhase(state: RTCPeerConnectionState | undefined, withoutPeerMs: number): PeerState {
  if (state === 'connected') return 'connected'
  if (state === 'failed' || state === 'closed') return 'failed'
  if (withoutPeerMs >= FAILED_AFTER_MS) return 'failed'
  if (state === 'disconnected') return withoutPeerMs >= RECONNECTING_AFTER_MS ? 'reconnecting' : 'connected'
  return 'connecting'
}

export interface StageRoster {
  /** Everyone else in the meeting. */
  people: readonly string[]
  sharing: string | null
}

/** What changed between two moments of a meeting, said once, with the name. */
export function announcements(
  before: StageRoster,
  after: StageRoster,
  nameOf: (id: string) => string,
): string[] {
  const lines: string[] = []
  for (const id of after.people) if (!before.people.includes(id)) lines.push(`${nameOf(id)} joined`)
  for (const id of before.people) if (!after.people.includes(id)) lines.push(`${nameOf(id)} left`)
  if (before.sharing !== after.sharing) {
    if (before.sharing !== null && after.sharing === null) lines.push(`${nameOf(before.sharing)} stopped sharing`)
    if (after.sharing !== null) lines.push(`${nameOf(after.sharing)} started sharing`)
  }
  return lines
}
